import React from 'react';
import { Tooltip, Modal } from 'antd';
import { SettingOutlined, UndoOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useOverviewDefaultConfig } from '../../../hooks/useAdminControls';
import { useCustomerLevelDefaultLandingPageConfig } from '@hooks/useCustomerLevelDefaultLandingPage';
import { useCustomerDefaultLandingPageInfo } from '@hooks/useCustomerDefaultLandingPageInfo';
import { checkIsAdminUser, checkIsInternalUser } from '@src/globalStore/profileStore';
import {
  Header,
  AdminContainer,
  SectionContainer,
  SectionHeader,
  SectionContent,
  AdminButton,
  InfoBox,
} from './styled';

const AdminControls: React.FC = () => {
  const isAdmin = checkIsAdminUser();
  const isInternal = checkIsInternalUser();

  const enableApplyAll = isAdmin && isInternal;

  const { applyToAll, removeCustomerDefault, isSettingDefault, isDeletingDefault } =
    useOverviewDefaultConfig();

  // Add landing page functionality
  const {
    applyToAll: applyLandingPageToAll,
    removeCustomerDefault: removeLandingPageDefault,
    isSettingDefault: isSavingDefaultLandingPage,
    isDeletingDefault: isDeletingDefaultLandingPage,
  } = useCustomerLevelDefaultLandingPageConfig();

  const { customerDefaultInfo, refetch: refetchCustomerDefault } =
    useCustomerDefaultLandingPageInfo();

  // Check if current page (overview) is the customer default landing page
  const { pathname } = window.location;
  const isCurrentPageCustomerDefault =
    customerDefaultInfo?.exists &&
    customerDefaultInfo?.path &&
    customerDefaultInfo.path === pathname;

  const handleApplyToAll = () => {
    Modal.confirm({
      title: 'Apply Configuration to All Users',
      content:
        'This will set the current configuration as the default for all first-time users in this account. Users with existing personalized settings will not be affected by this change.',
      okText: 'Apply to All',
      cancelText: 'Cancel',
      onOk: applyToAll,
    });
  };

  const handleRemoveDefault = () => {
    Modal.confirm({
      title: 'Revert to Conviva Default',
      content:
        "This will remove the customer default configuration. Users without personalized settings will see Conviva's system default instead.",
      okText: 'Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: removeCustomerDefault,
    });
  };

  const handleSaveDefaultLandingPageForAll = () => {
    Modal.confirm({
      title: 'Set Default Landing Page for All Users',
      content:
        'This will set Overview dashboard as the default landing page for all first-time users in this account. Users with existing personalized landing pages will not be affected by this change.',
      okText: 'Set as Default',
      cancelText: 'Cancel',
      onOk: async () => {
        await applyLandingPageToAll();
        refetchCustomerDefault();
      },
    });
  };

  const handleRemoveDefaultLandingPage = () => {
    Modal.confirm({
      title: 'Remove Default Landing Page',
      content:
        'This will remove the customer default landing page. Users without personalized landing pages will see the system default instead.',
      okText: 'Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        await removeLandingPageDefault();
        refetchCustomerDefault();
      },
    });
  };

  // Only show admin controls for admin users
  if (!enableApplyAll) {
    return null;
  }

  return (
    <AdminContainer direction="vertical">
      <Header>Admin Controls</Header>

      {/* Persistence Section */}
      <SectionContainer>
        <SectionHeader>Persistence</SectionHeader>
        <SectionContent direction="vertical">
          <Tooltip title="Set current configuration as default for all users">
            <AdminButton
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={handleApplyToAll}
              loading={isSettingDefault}
            >
              Apply to all
            </AdminButton>
          </Tooltip>

          <Tooltip title="Remove customer default configuration for all users">
            <AdminButton
              type="text"
              size="small"
              icon={<UndoOutlined />}
              onClick={handleRemoveDefault}
              loading={isDeletingDefault}
            >
              Remove for all
            </AdminButton>
          </Tooltip>
        </SectionContent>
      </SectionContainer>
      {/* Landing Page Section */}
      <SectionContainer marginTop="16px">
        <SectionHeader>Landing Page</SectionHeader>

        {/* Show current customer default info */}
        {customerDefaultInfo?.exists && isCurrentPageCustomerDefault && (
          <InfoBox>
            <InfoCircleOutlined style={{ margin: 4, color: '#667eea' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Current default for all users</div>
            </div>
          </InfoBox>
        )}

        <SectionContent direction="vertical">
          <Tooltip title="Set Overview dashboard as default landing page for all">
            <AdminButton
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={handleSaveDefaultLandingPageForAll}
              loading={isSavingDefaultLandingPage}
            >
              Apply to all
            </AdminButton>
          </Tooltip>

          <Tooltip title="Remove default landing page for all">
            <AdminButton
              type="text"
              size="small"
              icon={<UndoOutlined />}
              onClick={handleRemoveDefaultLandingPage}
              loading={isDeletingDefaultLandingPage}
              disabled={!customerDefaultInfo?.exists}
            >
              Remove for all
            </AdminButton>
          </Tooltip>
        </SectionContent>
      </SectionContainer>
    </AdminContainer>
  );
};

export default AdminControls;
